import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import React from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { useReactToPrint } from "react-to-print";

const headerStyle = {
  p: 1,
  textAlign: "center",
  bgcolor: "#4527a0",
  color: "#FFF",
  border: "1px solid black",
  fontSize: "0.8rem",
  fontWeight: "bold",
};

const cellStyle = {
  p: 0.6,
  border: "1px solid #ccc",
  fontSize: "0.75rem",
};

function CashFlowReportPrint() {
  const { companyInfo } = useSelector((state) => state?.common);
  const logo = JSON.parse(companyInfo?.logo || "{}");
  const location = useLocation();
  const { tabledata, fromDate, toDate, godown, withoutOpening } =
    location.state || {};
  const navigate = useNavigate();
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: () => {
      navigate(-1);
    },
  });

  useEffect(() => {
    if (tabledata) {
      handlePrint();
    }
  }, [handlePrint, tabledata]);

  const columns = [
    { label: "Date", key: "date", width: "10%" },
    { label: "Particulars", key: "particulars", width: "35%" },
    { label: "Vch Typ", key: "vchTyp", width: "5%" },
    { label: "Vch No", key: "vchNo", width: "10%" },
    { label: "Debit", key: "debit", width: "10%" },
    { label: "Credit", key: "credit", width: "10%" },
    { label: "Narration", key: "narration", width: "40%" },
  ];

  if (!tabledata) {
    return <Typography>No data to print</Typography>;
  }

  return (
    <Box p={3} ref={componentRef}>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        mb={3}
      >
        <Box px={2}>
          {logo?.img && (
            <Box
              component={"img"}
              sx={{ height: "auto", width: 100 }}
              src={logo?.img}
            />
          )}
        </Box>
        <Box textAlign="center">
          <Typography variant="h4" fontWeight={"bold"}>
            Cash Flow Report
          </Typography>
          <Typography variant="body1">
            {fromDate} - {toDate}
          </Typography>
          {godown && <Typography variant="body2">Outlet: {godown}</Typography>}
          {withoutOpening !== undefined && (
            <Typography variant="body2">
              {withoutOpening ? "Without Opening" : "Opening Included"}
            </Typography>
          )}
        </Box>
        <Box px={2} textAlign="right">
          <Typography variant="h5" fontWeight="bold">
            {companyInfo?.Name}
          </Typography>
          <Typography variant="body2">
            {companyInfo?.Add1} {companyInfo?.Add2}
          </Typography>
          <Typography variant="body2">{companyInfo?.Ph}</Typography>
          <Typography variant="body2">
            {companyInfo?.city_name} {companyInfo?.Place}
          </Typography>
          <Typography variant="body2">{companyInfo?.EMail}</Typography>
        </Box>
      </Stack>

      <TableContainer>
        <Table size="small" sx={{ border: "1px solid black" }}>
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell key={idx} sx={{ ...headerStyle, width: col.width }}>
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tabledata.map((row, idx) => (
              <TableRow
                key={idx}
                sx={{ backgroundColor: row.isBold ? "#f5f5f5" : "inherit" }}
              >
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "center",
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.date || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "left",
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.particulars || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "center",
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.vchTyp?.toLowerCase() === "receipt"
                    ? "RV"
                    : row.vchTyp?.toLowerCase() === "payment"
                      ? "PV"
                      : row.vchTyp || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "center",
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.vchNo || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "right",
                    pr: 1,
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.debit || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "right",
                    pr: 1,
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.credit || ""}
                </TableCell>
                <TableCell
                  sx={{
                    ...cellStyle,
                    textAlign: "left",
                    fontWeight: row.isBold ? "bold" : "normal",
                  }}
                >
                  {row.narration || ""}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default CashFlowReportPrint;
